// search-settings.ts — CTSK-504 (search telemetry + tuning).
//
// Nine per-org settings under section `documents` that tune document search
// WITHOUT a deploy or re-index. Defaults reproduce today's behaviour exactly.
//
//   • searchRankWeights          ts_rank_cd weights [D, C, B, A] (each 0–1)
//   • searchTrgmFallback         include the substring (trigram) CTE
//   • searchHeadlineChars        content chars ts_headline sees (5k–200k)
//   • searchStatementTimeoutMs   SET LOCAL statement_timeout (500–15000)
//   • searchSlowMs               slow-search warn threshold (50–10000)
//   • searchLogQueries           log truncated query text (may be personal data)
//   • searchLabelBoost           CTSK-506: weight of group/collection/folder text in the
//                                rank (0–5, default 1; 0 = labels match but add no rank)
//   • searchFacetsEnabled        CTSK-507: compute meta.facets (group / collection / folder
//                                counts) on GET /documents/search; default true
//   • neighborhoodMaxPerBasis    CTSK-507: default `limit` of GET /documents/:id/neighborhood
//                                (5–100, default 50); an explicit ?limit= still wins
//
// ONE normaliser, applied on GET and PATCH /settings/documents (clamp on read
// AND write, same as grouping-settings.ts) and mirrored by the web form.
/** Postgres' own default for ts_rank_cd: {D, C, B, A}. */
export const SEARCH_RANK_WEIGHTS_DEFAULT = [0.1, 0.2, 0.4, 1.0];
export const SEARCH_RANK_WEIGHT_MIN = 0;
export const SEARCH_RANK_WEIGHT_MAX = 1;
export const SEARCH_TRGM_FALLBACK_DEFAULT = true;
export const SEARCH_HEADLINE_CHARS_DEFAULT = 50_000;
export const SEARCH_HEADLINE_CHARS_MIN = 5_000;
export const SEARCH_HEADLINE_CHARS_MAX = 200_000;
export const SEARCH_STATEMENT_TIMEOUT_MS_DEFAULT = 3_000;
export const SEARCH_STATEMENT_TIMEOUT_MS_MIN = 500;
export const SEARCH_STATEMENT_TIMEOUT_MS_MAX = 15_000;
export const SEARCH_SLOW_MS_DEFAULT = 500;
export const SEARCH_SLOW_MS_MIN = 50;
export const SEARCH_SLOW_MS_MAX = 10_000;
export const SEARCH_LOG_QUERIES_DEFAULT = false;
/** CTSK-506 — multiplier on the label vector's own contribution to the rank. */
export const SEARCH_LABEL_BOOST_DEFAULT = 1;
export const SEARCH_LABEL_BOOST_MIN = 0;
export const SEARCH_LABEL_BOOST_MAX = 5;
/** CTSK-507 — facet counts on the search response. Best-effort; turn off if `facets` dominates the latency. */
export const SEARCH_FACETS_ENABLED_DEFAULT = true;
/** CTSK-507 — default `limit` (items per basis) of the neighborhood endpoint. */
export const NEIGHBORHOOD_MAX_PER_BASIS_DEFAULT = 50;
export const NEIGHBORHOOD_MAX_PER_BASIS_MIN = 5;
export const NEIGHBORHOOD_MAX_PER_BASIS_MAX = 100;
export const SEARCH_TUNING_KEYS = [
    'searchRankWeights',
    'searchTrgmFallback',
    'searchHeadlineChars',
    'searchStatementTimeoutMs',
    'searchSlowMs',
    'searchLogQueries',
    'searchLabelBoost',
    'searchFacetsEnabled',
    'neighborhoodMaxPerBasis',
];
export const SEARCH_TUNING_DEFAULTS = {
    searchRankWeights: [...SEARCH_RANK_WEIGHTS_DEFAULT],
    searchTrgmFallback: SEARCH_TRGM_FALLBACK_DEFAULT,
    searchHeadlineChars: SEARCH_HEADLINE_CHARS_DEFAULT,
    searchStatementTimeoutMs: SEARCH_STATEMENT_TIMEOUT_MS_DEFAULT,
    searchSlowMs: SEARCH_SLOW_MS_DEFAULT,
    searchLogQueries: SEARCH_LOG_QUERIES_DEFAULT,
    searchLabelBoost: SEARCH_LABEL_BOOST_DEFAULT,
    searchFacetsEnabled: SEARCH_FACETS_ENABLED_DEFAULT,
    neighborhoodMaxPerBasis: NEIGHBORHOOD_MAX_PER_BASIS_DEFAULT,
};
function toNumber(value) {
    if (typeof value === 'number')
        return value;
    if (typeof value === 'string' && value.trim() !== '')
        return Number(value);
    return NaN;
}
function clampInt(value, min, max, fallback) {
    const n = toNumber(value);
    if (!Number.isFinite(n))
        return fallback;
    return Math.min(max, Math.max(min, Math.trunc(n)));
}
/** Finite number clamped 0–5 (fractions kept, rounded to 2 places); anything else → 1. */
export function normalizeSearchLabelBoost(value) {
    const n = toNumber(value);
    if (!Number.isFinite(n))
        return SEARCH_LABEL_BOOST_DEFAULT;
    return Math.round(Math.min(SEARCH_LABEL_BOOST_MAX, Math.max(SEARCH_LABEL_BOOST_MIN, n)) * 100) / 100;
}
/** Integer clamped 5–100 (fractions truncated); anything else → 50. */
export function normalizeNeighborhoodMaxPerBasis(value) {
    return clampInt(value, NEIGHBORHOOD_MAX_PER_BASIS_MIN, NEIGHBORHOOD_MAX_PER_BASIS_MAX, NEIGHBORHOOD_MAX_PER_BASIS_DEFAULT);
}
/** Exactly four finite numbers, each clamped 0–1; anything else → default. */
export function normalizeSearchRankWeights(value) {
    if (!Array.isArray(value) || value.length !== 4)
        return [...SEARCH_RANK_WEIGHTS_DEFAULT];
    const out = value.map(toNumber);
    if (out.some((n) => !Number.isFinite(n)))
        return [...SEARCH_RANK_WEIGHTS_DEFAULT];
    const c = out.map((n) => Math.min(SEARCH_RANK_WEIGHT_MAX, Math.max(SEARCH_RANK_WEIGHT_MIN, n)));
    return [c[0], c[1], c[2], c[3]];
}
/** Real booleans only ("false" the string is NOT truthy here); else default. */
export function normalizeSearchBoolean(value, fallback) {
    return typeof value === 'boolean' ? value : fallback;
}
export function normalizeSearchHeadlineChars(value) {
    return clampInt(value, SEARCH_HEADLINE_CHARS_MIN, SEARCH_HEADLINE_CHARS_MAX, SEARCH_HEADLINE_CHARS_DEFAULT);
}
export function normalizeSearchStatementTimeoutMs(value) {
    return clampInt(value, SEARCH_STATEMENT_TIMEOUT_MS_MIN, SEARCH_STATEMENT_TIMEOUT_MS_MAX, SEARCH_STATEMENT_TIMEOUT_MS_DEFAULT);
}
export function normalizeSearchSlowMs(value) {
    return clampInt(value, SEARCH_SLOW_MS_MIN, SEARCH_SLOW_MS_MAX, SEARCH_SLOW_MS_DEFAULT);
}
/** The nine tuning settings, defaulted and clamped, from a raw `documents` settings body. */
export function normalizeSearchTuningSettings(body) {
    const b = (body && typeof body === 'object' ? body : {});
    return {
        searchRankWeights: normalizeSearchRankWeights(b.searchRankWeights),
        searchTrgmFallback: normalizeSearchBoolean(b.searchTrgmFallback, SEARCH_TRGM_FALLBACK_DEFAULT),
        searchHeadlineChars: normalizeSearchHeadlineChars(b.searchHeadlineChars),
        searchStatementTimeoutMs: normalizeSearchStatementTimeoutMs(b.searchStatementTimeoutMs),
        searchSlowMs: normalizeSearchSlowMs(b.searchSlowMs),
        searchLogQueries: normalizeSearchBoolean(b.searchLogQueries, SEARCH_LOG_QUERIES_DEFAULT),
        searchLabelBoost: normalizeSearchLabelBoost(b.searchLabelBoost),
        searchFacetsEnabled: normalizeSearchBoolean(b.searchFacetsEnabled, SEARCH_FACETS_ENABLED_DEFAULT),
        neighborhoodMaxPerBasis: normalizeNeighborhoodMaxPerBasis(b.neighborhoodMaxPerBasis),
    };
}
/** Which tuning keys differ from the default (for diagnostics "overridden"). */
export function overriddenSearchTuningKeys(s) {
    return SEARCH_TUNING_KEYS.filter((k) => JSON.stringify(s[k]) !== JSON.stringify(SEARCH_TUNING_DEFAULTS[k]));
}
//# sourceMappingURL=search-settings.js.map